import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Star, Trash2, Eye, EyeOff } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { reviewsApi } from "../api";

const EMPTY = { name: "", rating: 5, text: "", date: "", source: "Google", publicDisplay: true };

const ReviewsPanel = () => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => reviewsApi.list().then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await reviewsApi.create({ ...form, rating: Number(form.rating) });
      toast({ title: "Review added", description: "It now shows on the homepage." });
      setForm(EMPTY);
      load();
    } catch {
      toast({ title: "Could not add review", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const toggleDisplay = async (r) => {
    await reviewsApi.update(r.id, { publicDisplay: !r.publicDisplay });
    load();
  };

  const remove = async (id) => {
    await reviewsApi.remove(id);
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg text-[#0E2A33]">Add a Google review</h3>
        <p className="text-xs text-slate-500 mt-1">
          Copy real reviews from your Google Business Profile. Only add reviews that actually exist — never invent them.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Reviewer name</Label>
              <Input data-testid="review-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. Sarah M." />
            </div>
            <div>
              <Label>Rating</Label>
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
                <SelectTrigger data-testid="review-rating-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Review text</Label>
            <Textarea data-testid="review-text-input" required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-1" rows={4} placeholder="Paste the review exactly as written on Google…" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div>
              <Label>Review date (as shown on Google)</Label>
              <Input data-testid="review-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" placeholder="e.g. May 2026" />
            </div>
            <div className="flex items-center gap-3 pb-2">
              <Switch checked={form.publicDisplay} onCheckedChange={(v) => setForm({ ...form, publicDisplay: v })} />
              <span className="text-sm text-slate-600">Show on homepage</span>
            </div>
          </div>
          <Button data-testid="review-submit-btn" type="submit" disabled={busy} className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-11 font-semibold">
            {busy ? "Adding…" : "Add Review"}
          </Button>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg text-[#0E2A33]">Published reviews ({reviews.length})</h3>
        <div className="mt-4 space-y-3 max-h-[520px] overflow-y-auto">
          {reviews.length === 0 && <p className="text-sm text-slate-400">No reviews yet. Add your first Google review on the left — the homepage section appears automatically.</p>}
          {reviews.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${r.publicDisplay ? "bg-[#F7FBFC] border-slate-100" : "bg-slate-50 border-slate-200 opacity-60"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#0E2A33] text-sm">{r.name}</span>
                  <span className="flex items-center gap-0.5 text-amber-500 text-xs font-semibold"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {r.rating}</span>
                  {r.date && <span className="text-xs text-slate-400">{r.date}</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid="review-toggle-btn" onClick={() => toggleDisplay(r)} className="p-1.5 text-slate-400 hover:text-[#0E5A72]" title={r.publicDisplay ? "Hide from homepage" : "Show on homepage"}>
                    {r.publicDisplay ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button data-testid="review-delete-btn" onClick={() => remove(r.id)} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">"{r.text}"</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewsPanel;
